import { fmtMoney } from "@/lib/api";
import { Card } from "./Card";

/**
 * HealthCard — large calm metric tile.
 *
 *  Props:
 *    label         — small caps label
 *    value         — big formatted value (you control formatting)
 *    tone          — default | positive | warning | negative
 *    movement      — { delta, label, positiveIsGood? }
 *    confidence    — high | medium | low
 *    interpretation — short prose under the number
 *    testId
 */
const TONE_CLS = {
    default: "text-stone-900",
    positive: "text-emerald-700",
    warning: "text-amber-700",
    negative: "text-red-700",
};

const CONF_TONE = {
    high: "bg-emerald-50 text-emerald-700 border-emerald-100",
    medium: "bg-amber-50 text-amber-700 border-amber-100",
    low: "bg-red-50 text-red-700 border-red-100",
};

export function HealthCard({
    label,
    value,
    tone = "default",
    movement,
    confidence,
    interpretation,
    money = true,
    testId,
    onClick,
}) {
    const formatted =
        typeof value === "number"
            ? money
                ? fmtMoney(value)
                : value.toString()
            : value ?? "—";

    const positiveIsGood = movement?.positiveIsGood !== false;
    const positive = movement?.delta != null && movement.delta >= 0;
    const movementGood = positive === positiveIsGood;
    const arrow = movement?.delta == null
        ? null
        : positive
            ? "↑"
            : "↓";

    return (
        <Card
            variant="default"
            padding="md"
            className={`transition ${onClick ? "cursor-pointer hover:border-stone-300" : ""}`}
            testId={testId}
            onClick={onClick}
        >
            <div className="flex items-center justify-between mb-3">
                <div className="text-[12px] text-stone-500 font-medium">{label}</div>
                {confidence && (
                    <span
                        className={`text-[10px] uppercase tracking-wider font-medium border rounded-full px-1.5 py-0.5 ${
                            CONF_TONE[confidence] || CONF_TONE.medium
                        }`}
                    >
                        {confidence}
                    </span>
                )}
            </div>
            <div
                className={`font-mono-num text-[28px] leading-[1.05] font-medium tracking-tight ${TONE_CLS[tone]}`}
            >
                {formatted}
            </div>
            {movement?.delta != null && (
                <div
                    className={`mt-2 inline-flex items-center gap-1.5 text-[12px] font-medium ${
                        movementGood ? "text-emerald-700" : "text-red-700"
                    }`}
                >
                    <span>{arrow}</span>
                    <span className="font-mono-num">
                        {money ? fmtMoney(Math.abs(movement.delta)) : Math.abs(movement.delta)}
                    </span>
                    {movement.label && (
                        <span className="text-stone-500 font-normal">
                            {movement.label}
                        </span>
                    )}
                </div>
            )}
            {interpretation && (
                <div className="mt-2 text-[13px] text-stone-500 leading-snug">
                    {interpretation}
                </div>
            )}
        </Card>
    );
}
